import axios from 'axios';

import { AppEnv } from 'core/enums';
import {
  getFormData,
  objectKeysCamelToSnakeCase,
  objectKeysSnakeCaseToCamelCase,
} from 'core/utils';
import { APP_ENV, PHPSESSID } from 'global';

interface IParams {
  method: string;
  path: string;
  data?;
  options?;
  signal?; // used for aborting previous pending requests
}

const GET = 'get';
const POST = 'post';
const PUT = 'put';
const PATCH = 'patch';
const DELETE = 'delete';

const METHODS_WITH_DATA = [POST, PUT, PATCH];

const apiClient = axios.create({
  baseURL: '',
  withCredentials: true,
});

export const get = (path, signal?) => {
  return request({
    method: GET,
    path,
    signal,
  });
};

export const post = (path: string, data?, options?, signal?) => {
  return request({
    method: POST,
    path,
    data,
    options,
    signal,
  });
};

type TPostFormOptions = {
  requestOptions?: unknown;
  signal?: AbortSignal;
  toSnakeCase?: (key: string) => string;
  excludeKeys?: string[];
  includeNullValues?: boolean;
  skipResultMapping?: boolean;
};

export const postForm = async (
  path: string,
  data,
  {
    requestOptions,
    signal,
    toSnakeCase,
    excludeKeys = [],
    includeNullValues = false,
    skipResultMapping = false,
  }: TPostFormOptions = {}
) => {
  const payload = getFormData(
    objectKeysCamelToSnakeCase(data, { toSnakeCase, excludeKeys }),
    includeNullValues
  );

  const result = await post(path, payload, requestOptions, signal);

  if (result.data?.cliJob) {
    result.data.cliJob.result = objectKeysSnakeCaseToCamelCase(
      JSON.parse((result.data.cliJob.result as string) || '{}')
    );
  }

  return skipResultMapping ? result.data : objectKeysSnakeCaseToCamelCase(result.data);
};

export type TPostJsonOptions = {
  requestOptions?: Record<string, unknown>;
  signal?: AbortSignal;
  toSnakeCase?: (string) => string;
  skipResultMapping?: boolean;
};

export const postJson = async (
  path: string,
  data: Record<string, unknown>,
  { requestOptions = {}, signal, toSnakeCase, skipResultMapping = false }: TPostJsonOptions = {}
) => {
  const payload = JSON.stringify(objectKeysCamelToSnakeCase(data, { toSnakeCase }));

  const result = await post(
    path,
    payload,
    {
      'Content-Type': 'application/json',
      ...requestOptions,
    },
    signal
  );

  return skipResultMapping ? result.data : objectKeysSnakeCaseToCamelCase(result.data);
};

export const patch = (path, data) => {
  return request({
    method: PATCH,
    path,
    data,
  });
};

export const remove = (path, data?) => {
  return request({
    method: DELETE,
    path,
    data,
  });
};

export const request = async ({ method, path, data, options, signal }: IParams) => {
  const cookie = APP_ENV === AppEnv.DEVELOPMENT ? { phpsessid: PHPSESSID } : {};

  const config = {
    headers: {
      'x-requested-with': 'XMLHttpRequest',
      ...cookie,
      ...options,
    },
    signal,
  };

  try {
    if (METHODS_WITH_DATA.includes(method)) {
      return await apiClient[method](path, data, config);
    }

    if (DELETE === method && data) {
      return await apiClient[method](path, { data, ...config });
    }

    return await apiClient[method](path, config);
  } catch (error) {
    throw objectKeysSnakeCaseToCamelCase(error);
  }
};

export const partfinitiClient = {
  get,
  post,
  postForm,
  postJson,
  patch,
  remove,
};
